import React, { useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { setExploreSaveViewDialogParameters, saveExploreView,
  setExploreSelectedViewId, setExploreSelectedView, setExploreSelectedViewIsOpen, createExplorePropertyDataObjectInstance, 
  setExplorePins, setIsRedirectToExplore, setExploreLocation } from 'store/actions';
import { sendClientLog } from 'services/logService';

const SaveViewDialog = ({ classes, user, canSave, saveViewDialogParameters, selectedView, setExploreSaveViewDialogParameters,
  saveExploreView, setExploreSelectedView, setExploreSelectedViewId, createExplorePropertyDataObjectInstance, setExplorePins,
  setIsRedirectToExplore, setExploreLocation
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const handleSave = () => {
    setIsSaving(true);
    saveExploreView(() => {
      setIsSaving(false);
      navigateToView();
      sendClientLog('Info',`Explore Views - User saved view with id ${selectedView?.id} and navigated to view id ${saveViewDialogParameters.navigateToViewId}`);
    });
  };

  const handleDiscard = () => {
    navigateToView();
    sendClientLog('Info',`Explore Views - User discarded view with id ${selectedView?.id} and navigated to view id ${saveViewDialogParameters.navigateToViewId}`);
  }

  const navigateToView = () => {
    const { navigateToViewId, navigateToView } = saveViewDialogParameters;
    const isTempView = navigateToView?.id === 'temp';
    const isSystemView = navigateToView?.id === 'system';
    handleClose();
    
    const callback = () => {
      if (navigateToView) {
        setExploreSelectedView(navigateToView);
        if (isTempView || isSystemView) {
          const { zoomLevel, latitude, longitude, bounds } = navigateToView;
          if (selectedView.id === 'system') { // DT-158: Normally, the map is set to a view's location when selectedView.id changes in a ViewPanel.js useEffect. If both new view and discarded view are system views, id doesn't change (both 'system') and map won't go to system view's default nation-level location. Manually set it here so property data will be fetched and best fit performed.
            const center = latitude != null && longitude != null ? { lat: latitude, lng: longitude } : null;
            setExploreLocation({ zoom: zoomLevel, center, bounds}, false);
          }
          
          setExplorePins([]);
          createExplorePropertyDataObjectInstance(navigateToView.objects[0]);
          setIsRedirectToExplore(true);
        }
      }
    }
    setExploreSelectedViewId(navigateToViewId, !isTempView && !isSystemView, !isTempView || !navigateToView.fromExisting, callback); // Don't fetch if view to navigate to is a temp one, clear data objects unless creating a duplicate
  };

  const handleClose = () => setExploreSaveViewDialogParameters(false, null, null);
  const selectedViewHasNoObjects = selectedView?.objects.length === 0;
  const canSaveMessage = 'Switching to a different view will cause unsaved changes to be lost. Do you want to save before continuing?';
  const canNotSaveMessage = 'Switching to a different view will discard your changes, unless they are saved to a new View. Continue?';
  const hasNoObjectsMessage = 'Switching to a different view will cause your unsaved view to be discarded. Continue?';
  return (
    <Dialog
      open={true}
      keepMounted
      onClose={e => {
        handleClose();
      }}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle id="alert-dialog-slide-title">{'Save View?'}
      </DialogTitle>
      <DialogContent style={{ minHeight: 48, padding: '0 24px 8px' }}>
        {isSaving
          ? <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
          : <DialogContentText id="alert-dialog-slide-description">
            {selectedViewHasNoObjects ? hasNoObjectsMessage : canSave ? canSaveMessage : canNotSaveMessage}
          </DialogContentText>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" variant='outlined' disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleDiscard} color="primary" variant='contained' disabled={isSaving}>
          Discard
        </Button>
        {canSave && !selectedViewHasNoObjects &&
          <Button onClick={handleSave} color="primary" variant='contained' disabled={isSaving}>
            Save
          </Button>
        }
      </DialogActions>
    </Dialog>
  )
}

const mapStateToProps = (state) => ({
  user: state.appState.user,
  saveViewDialogParameters: state.exploreViews.saveViewDialogParameters,
  selectedView: state.exploreViews.selectedView
});

const mapDispatchToProps = {
  saveExploreView,
  setExploreSaveViewDialogParameters,
  setExploreSelectedView,
  setExploreSelectedViewId,
  setExploreSelectedViewIsOpen,
  createExplorePropertyDataObjectInstance,
  setExplorePins,
  setIsRedirectToExplore,
  setExploreLocation
};

const styles = theme => ({
  dialogPaper: {
    width: 400
  },
  redDialogButton: {
    color: 'white',
    backgroundColor: theme.palette.red.main,
    '&:hover': {
      backgroundColor: theme.palette.red.dark
    }
  },
});

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(SaveViewDialog);